import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Chip,
  OutlinedInput,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';

const engineerLabel = (engineer) =>
  `${engineer.firstName || ''} ${engineer.lastName || ''}`.trim() || engineer.email || engineer._id;

const idOf = (value) => (value && typeof value === 'object' ? String(value._id || '') : String(value || ''));

/**
 * Manager: set/update sequential-shift engineer team + current owner after accept.
 */
const MROAssignEngineersDialog = ({ open, onClose, workOrder, mroEngineers: engineersProp, onSuccess, useFacade = false }) => {
  const [assignedEngineerIds, setAssignedEngineerIds] = useState([]);
  const [currentEngineerId, setCurrentEngineerId] = useState('');
  const [assignedDate, setAssignedDate] = useState('');
  const [notes, setNotes] = useState('');
  const [mroEngineers, setMroEngineers] = useState(engineersProp || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { showToast } = useToast();

  useEffect(() => {
    if (Array.isArray(engineersProp) && engineersProp.length) {
      setMroEngineers(engineersProp);
    }
  }, [engineersProp]);

  useEffect(() => {
    if (!open) return;
    const fromArray = Array.isArray(workOrder?.mroAssignedEngineerIds)
      ? workOrder.mroAssignedEngineerIds.map(idOf)
      : [];
    const legacy = workOrder?.mroAssignedEngineerId ? [idOf(workOrder.mroAssignedEngineerId)] : [];
    const team = [...new Set([...fromArray, ...legacy].filter(Boolean))];
    const current = idOf(workOrder?.mroCurrentEngineerId || workOrder?.mroAssignedEngineerId) || team[0] || '';
    setAssignedEngineerIds(team);
    setCurrentEngineerId(current);
    setAssignedDate(workOrder?.mroAssignedDate ? String(workOrder.mroAssignedDate).slice(0, 10) : '');
    setNotes('');
    setError('');

    if (Array.isArray(engineersProp) && engineersProp.length) return undefined;
    let ignore = false;
    (async () => {
      try {
        const response = await axios.get('/api/users');
        const users = Array.isArray(response.data) ? response.data : [];
        if (!ignore) setMroEngineers(users.filter((u) => u?.role === 'MRO_ENGINEER'));
      } catch {
        if (!ignore) setMroEngineers([]);
      }
    })();
    return () => { ignore = true; };
  }, [open, workOrder, engineersProp]);

  const handleTeamChange = (event) => {
    const value = typeof event.target.value === 'string'
      ? event.target.value.split(',')
      : event.target.value;
    setAssignedEngineerIds(value);
    if (currentEngineerId && !value.includes(currentEngineerId)) {
      setCurrentEngineerId(value[0] || '');
    } else if (!currentEngineerId && value.length) {
      setCurrentEngineerId(value[0]);
    }
  };

  const handleClose = () => {
    setError('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!assignedEngineerIds.length) {
      setError('Select at least one engineer');
      return;
    }
    if (!currentEngineerId || !assignedEngineerIds.includes(currentEngineerId)) {
      setError('Current shift engineer must be one of the assigned engineers');
      return;
    }
    try {
      setLoading(true);
      setError('');
      const endpoint = useFacade
        ? `/api/mro/${workOrder._id}/assign-engineers`
        : `/api/maintenance/${workOrder._id}/mro/assign-engineers`;
      await axios.post(endpoint, {
        assignedEngineerIds,
        currentEngineerId,
        assignedDate: assignedDate || undefined,
        notes: notes.trim() || undefined
      });
      showToast('Engineer team updated', 'success');
      onSuccess?.();
      handleClose();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to assign engineers';
      setError(message);
      showToast(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assign shift engineers</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Work Order: <strong>{workOrder?.title || workOrder?.workOrderNumber}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Assign one or more engineers. A second engineer is optional — with one assignee, they can hand over to any MRO engineer.
            Selecting 2+ engineers locks handover to that team.
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <FormControl fullWidth sx={{ mt: 1 }}>
          <InputLabel id="mro-team-assign-label">Engineer team</InputLabel>
          <Select
            labelId="mro-team-assign-label"
            multiple
            value={assignedEngineerIds}
            onChange={handleTeamChange}
            input={<OutlinedInput label="Engineer team" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((id) => {
                  const eng = mroEngineers.find((e) => e._id === id);
                  return <Chip key={id} size="small" label={eng ? engineerLabel(eng) : id} />;
                })}
              </Box>
            )}
          >
            {mroEngineers.map((engineer) => (
              <MenuItem key={engineer._id} value={engineer._id}>
                {engineerLabel(engineer)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {assignedEngineerIds.length > 0 && (
          <TextField
            select
            fullWidth
            label="Current shift engineer"
            value={currentEngineerId || assignedEngineerIds[0] || ''}
            onChange={(e) => setCurrentEngineerId(e.target.value)}
            sx={{ mt: 2 }}
          >
            {assignedEngineerIds.map((id) => {
              const eng = mroEngineers.find((e) => e._id === id);
              return (
                <MenuItem key={id} value={id}>
                  {eng ? engineerLabel(eng) : id}
                </MenuItem>
              );
            })}
          </TextField>
        )}

        <TextField
          fullWidth
          type="date"
          label="Assign date (optional)"
          value={assignedDate}
          onChange={(e) => setAssignedDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ mt: 2 }}
        />
        <TextField
          fullWidth
          multiline
          rows={2}
          label="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !assignedEngineerIds.length}
        >
          {loading ? 'Saving...' : 'Save team'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MROAssignEngineersDialog;
